import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { billingApi } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import "./AcceptInvite.css";

/**
 * Page for accepting a plan invite.
 * Accessed via /invite?token=...
 *
 * Flow:
 * 1. Validates the invite token against the backend
 * 2. Shows plan details to the user
 * 3. User clicks "Accept & Subscribe" to go through Stripe checkout
 */
const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // loading | ready | confirming | processing | error | already
  const [error, setError] = useState("");
  const [inviteData, setInviteData] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError("Invalid invite link. No token provided.");
      return;
    }

    const controller = new AbortController();
    billingApi
      .validateInvite(token, { signal: controller.signal })
      .then((data) => {
        // data is already unwrapped by the axios response interceptor (api.js),
        // so data.alreadyOnPlan etc. are direct properties — no .data nesting.
        setInviteData(data);
        if (data.alreadyOnPlan) {
          setStatus("already");
        } else {
          setStatus("ready");
        }
      })
      .catch((err) => {
        if (err.name === "CanceledError") return;
        setStatus("error");
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to validate invite.",
        );
      });

    return () => controller.abort();
  }, [token]);

  const handleAccept = async () => {
    setStatus("processing");
    try {
      const origin = window.location.origin;
      const response = await billingApi.createInviteCheckout(
        token,
        `${origin}/settings?billing=success`,
        `${origin}/invite?token=${encodeURIComponent(token)}`,
      );

      if (response.alreadyOnPlan) {
        setStatus("already");
        return;
      }

      // Redirect to Stripe checkout (response is pre-unwrapped by axios interceptor)
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (err) {
      setStatus("error");
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to start checkout.",
      );
    }
  };

  const formatPrice = (price) => {
    if (price === null) return "Custom";
    if (price === 0) return "Free";
    return `$${price}/mo`;
  };

  const formatLimit = (value) =>
    value === null ? "Unlimited" : value.toLocaleString();

  const getButtonText = () => {
    if (status === "processing") return "Processing...";
    if (status === "confirming")
      return `Confirm — Subscribe for ${formatPrice(inviteData?.planPrice)}`;
    return `Accept & Subscribe — ${formatPrice(inviteData?.planPrice)}`;
  };

  if (status === "loading") {
    return (
      <div className="accept-invite-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-card">
          <h2>Invite Error</h2>
          <p className="accept-invite-error">{error}</p>
          <button
            className="accept-invite-btn accept-invite-btn-secondary"
            onClick={() => navigate("/dashboard")}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (status === "already") {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-card">
          <h2>Already Subscribed</h2>
          <p>
            You are already on the <strong>{inviteData?.planName}</strong> plan.
          </p>
          <button
            className="accept-invite-btn accept-invite-btn-secondary"
            onClick={() => navigate("/dashboard")}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invite-page">
      <div className="accept-invite-card">
        <h2>You've Been Invited!</h2>
        <p>
          You've been invited to join the{" "}
          <strong>{inviteData?.planName}</strong> plan.
        </p>

        <div className="accept-invite-price">
          {formatPrice(inviteData?.planPrice)}
        </div>

        {inviteData?.planLimits && (
          <div className="accept-invite-limits">
            <div className="accept-invite-limit">
              <span className="accept-invite-limit-value">
                {formatLimit(inviteData.planLimits.widgets)}
              </span>
              <span className="accept-invite-limit-label">Widgets</span>
            </div>
            <div className="accept-invite-limit">
              <span className="accept-invite-limit-value">
                {formatLimit(inviteData.planLimits.messagesPerMonth)}
              </span>
              <span className="accept-invite-limit-label">Messages/mo</span>
            </div>
            <div className="accept-invite-limit">
              <span className="accept-invite-limit-value">
                {formatLimit(inviteData.planLimits.knowledgeSources)}
              </span>
              <span className="accept-invite-limit-label">
                Knowledge Sources
              </span>
            </div>
          </div>
        )}

        {inviteData?.planFeatures?.length > 0 && (
          <ul className="accept-invite-features">
            {inviteData.planFeatures.map((feature, i) => (
              <li key={i}>{feature}</li>
            ))}
          </ul>
        )}

        {inviteData?.checkoutEligible ? (
          <>
            <button
              className="accept-invite-btn accept-invite-btn-primary"
              onClick={
                status === "confirming"
                  ? handleAccept
                  : () => setStatus("confirming")
              }
              disabled={status === "processing"}
            >
              {getButtonText()}
            </button>

            {status === "confirming" && (
              <button
                className="accept-invite-btn accept-invite-btn-secondary"
                onClick={() => setStatus("ready")}
                style={{ marginTop: 0 }}
              >
                Cancel
              </button>
            )}

            {status === "processing" && (
              <p className="accept-invite-processing-hint">
                Redirecting to secure checkout…
              </p>
            )}
          </>
        ) : (
          <p className="accept-invite-error">
            This plan is not yet configured for checkout. Please contact the
            administrator who sent your invite.
          </p>
        )}

        <button
          className="accept-invite-btn accept-invite-btn-secondary"
          onClick={() => navigate("/dashboard")}
          disabled={status === "processing"}
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default AcceptInvite;
